import { cn } from '@cobliteam/colib-ui/cn';
import Typography from '@cobliteam/colib-ui/Typography';
import { useTranslation } from '@cobliteam/i18n';
import TooltipOnIntent from '@cobliteam/ui-business/components/TooltipOnIntent/TooltipOnIntent';
import { type CSSProperties, forwardRef, type ReactNode, useId, useState } from 'react';
import { tv } from 'tailwind-variants';
import AIIcon from '../AIIcon';

/** `solid`: full gradient bg + white sparkles. `soft`: frosted gradient bg + gradient sparkles. `ghost`: no background. `ai-outline`: white bg + gradient border. */
export type AIButtonVariant = 'solid' | 'soft' | 'ghost' | 'ai-outline';

export type AIButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/** `rounded`/`circle`: icon-only. `cta`: wide button with sparkles + label. */
export type AIButtonShape = 'rounded' | 'circle' | 'cta';

export type AIButtonProps = {
  showTooltip?: boolean;
  className?: string;
  dataTestId?: string;
  /** Native `data-testid`. Takes precedence over {@link AIButtonProps.dataTestId} when both are set. */
  'data-testid'?: string;
  /** Overrides the default accessible name (derived from `label` / i18n). */
  'aria-label'?: string;
  disabled?: boolean;
  size?: AIButtonSize;
  style?: CSSProperties;
  /** Visual style. @default 'solid' */
  variant?: AIButtonVariant;
  /** Button shape / layout mode. @default 'rounded' */
  shape?: AIButtonShape;
  /**
   * Element rendered in place of the animated AI sparkles (e.g. `<Icon name="..." size="sm" />` from `@cobliteam/colib-ui/Icon`). Caller controls size.
   * - `undefined` (omitted): default animated AI sparkles.
   * - a node: that node, in place of the sparkles. On non-`solid` variants its stroke is repainted with the AI foreground gradient (matching the sparkles), so a caller `color` is overridden; on `solid` the node renders untouched.
   * - `null`: no icon at all — e.g. a label-only `cta` button.
   */
  icon?: ReactNode;
  /** Text displayed when shape === 'cta' */
  label?: ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
};

const ICON_SIZE_MAP: Record<AIButtonSize, number> = { xs: 22, sm: 32, md: 38, lg: 42, xl: 46, '2xl': 58 };

/** Sparkle px inside a `cta` button, per size. */
const CTA_ICON_SIZE_MAP: Record<AIButtonSize, number> = { xs: 18, sm: 22, md: 32, lg: 36, xl: 40, '2xl': 46 };

/** Label typography variant inside a `cta` button, per size. */
const CTA_LABEL_VARIANT: Record<AIButtonSize, 'label' | 'body'> = {
  xs: 'label',
  sm: 'body',
  md: 'body',
  lg: 'body',
  xl: 'body',
  '2xl': 'body',
};

// Cobli AI gradients — Figma "Cobli IA Colors" (node 707:2887).
/** Background gradient — solid fills (Contained primary). */
const AI_BACKGROUND_GRADIENT = 'linear-gradient(248deg, #8b5cf6 20%, #0248e9 60%, #001bb8 100%)';
// Foreground gradient stops `#7c3aed → #6d28d9 → #0248e9 → #327af6` (text, sparkles, borders); the angle sets the sweep direction per use site.
// `ai-outline` border gradient — blue (left) → purple (right), at 40% opacity over white so the ring reads as a soft tint
// (Figma "Border default", node 871:26968), not the full-strength sweep used by the icon. Same hue stops as the foreground gradient.
const AI_OUTLINE_BORDER_GRADIENT = 'linear-gradient(270deg, rgba(124, 58, 237, 0.4) 0%, rgba(109, 40, 217, 0.4) 15%, rgba(2, 72, 233, 0.4) 65%, rgba(50, 122, 246, 0.4) 100%)';

/** Frosted overlay — used for `soft` variant (Contained secondary). */
const SOFT_BACKGROUND_STYLE = {
  backgroundImage: `linear-gradient(90deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), ${AI_BACKGROUND_GRADIENT}`,
} as const;

/** White fill + gradient border via border-box clip — used for `ai-outline` (Border). Reversed sweep matches the icon stroke direction (Figma). */
const AI_OUTLINE_BACKGROUND_STYLE = {
  border: '1px solid transparent',
  backgroundImage: `linear-gradient(#fff, #fff), ${AI_OUTLINE_BORDER_GRADIENT}`,
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
} as const;

function getVariantStyle(variant: AIButtonVariant, style?: CSSProperties): CSSProperties | undefined {
  if (variant === 'soft') return { ...SOFT_BACKGROUND_STYLE, ...style };
  if (variant === 'ai-outline') return { ...AI_OUTLINE_BACKGROUND_STYLE, ...style };
  return style;
}

/**
 * Paints a custom stroke-based icon (e.g. a lucide `<Icon>` from `@cobliteam/ui`) with the AI foreground gradient,
 * so a caller-supplied icon matches the gradient sparkles non-solid variants use by default (Figma "Cobli IA Colors").
 *
 * The CSS `stroke: url(#…)` set via the `--ai-icon-gradient` custom property overrides the icon's `stroke="currentColor"`
 * (CSS wins over the presentation attribute). `userSpaceOnUse` spans the lucide 24×24 viewBox so the sweep stays unified
 * across every sub-path of the icon instead of restarting per shape.
 */
function GradientIcon({ children }: { children: ReactNode }) {
  const gradientId = `ai-icon-gradient-${useId().replace(/\W/g, '')}`;
  return (
    <span className='relative inline-flex [&_svg]:stroke-(--ai-icon-gradient)' style={{ '--ai-icon-gradient': `url(#${gradientId})` } as CSSProperties}>
      <svg aria-hidden className='pointer-events-none absolute size-0' xmlns='http://www.w3.org/2000/svg'>
        <defs>
          <linearGradient id={gradientId} x1='24' y1='12' x2='0' y2='12' gradientUnits='userSpaceOnUse'>
            <stop offset='0%' stopColor='#7c3aed' />
            <stop offset='15%' stopColor='#6d28d9' />
            <stop offset='65%' stopColor='#0248e9' />
            <stop offset='100%' stopColor='#327af6' />
          </linearGradient>
        </defs>
      </svg>
      {children}
    </span>
  );
}

// ─── CTA (shape === 'cta') ───────────────────────────────────────────────────

const aiButtonCtaStyles = tv({
  slots: {
    button:
      'group relative inline-flex pointer-events-auto shrink-0 cursor-pointer items-center justify-center rounded-[10px] shadow-[1px_5px_11px_0px_rgba(139,92,246,0.32)] transition-all hover:shadow-[0_0_25px_4px_rgba(139,92,246,0.24)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-link focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none disabled:ring-0 disabled:saturate-50 disabled:focus-visible:ring-ring',
    label: 'relative z-[1] whitespace-nowrap',
  },
  variants: {
    variant: {
      solid: {
        button: 'bg-[linear-gradient(248deg,#8b5cf6_20%,#0248e9_60%,#001bb8_100%)] text-white',
        label: 'text-white',
      },
      soft: {
        button: '',
        label: 'text-foreground',
      },
      ghost: {
        button: 'border-transparent shadow-none hover:shadow-none',
        label: 'text-muted-foreground',
      },
      'ai-outline': {
        button: 'rounded-lg border-transparent bg-background text-[#0248e9] shadow-[0_1px_2px_0_rgba(0,27,184,0.05)]',
        label: 'bg-[linear-gradient(90deg,#7c3aed_0%,#6d28d9_15%,#0248e9_65%,#327af6_100%)] bg-clip-text text-transparent',
      },
    },
    size: {
      xs: { button: 'h-7 gap-1 px-1.5 pr-2.5 py-1' },
      sm: { button: 'h-8 gap-1.5 px-1.5 pr-3 py-1.5' },
      md: { button: 'h-10 gap-2 px-2 pr-4 py-2' },
      lg: { button: 'h-11 gap-2 px-2.5 pr-5 py-2' },
      xl: { button: 'h-12 gap-2.5 px-3 pr-5 py-2.5' },
      '2xl': { button: 'h-14 gap-3 px-3.5 pr-6 py-3' },
    },
  },
  defaultVariants: {
    variant: 'solid' as AIButtonVariant,
    size: 'md' as AIButtonSize,
  },
});

type AIButtonCtaSlotProps = {
  ariaLabel: string;
  className?: string;
  dataTestId?: string;
  decorPaused: boolean;
  icon?: ReactNode;
  isDisabled: boolean;
  isHovered: boolean;
  isSelected: boolean;
  label: ReactNode;
  size: AIButtonSize;
  style?: CSSProperties;
  variant: AIButtonVariant;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

const AIButtonCtaSlot = forwardRef<HTMLButtonElement, AIButtonCtaSlotProps>(function AIButtonCtaSlot(
  { ariaLabel, className, dataTestId, decorPaused, icon, isDisabled, isHovered, isSelected, label, size, style, variant, onClick, onMouseEnter, onMouseLeave },
  ref
) {
  const isSolid = variant === 'solid';
  const { button, label: labelStyles } = aiButtonCtaStyles({ variant, size });
  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      className={cn(button(), className)}
      data-testid={dataTestId ?? 'ai-button-cta'}
      disabled={isDisabled}
      ref={ref}
      style={getVariantStyle(variant, style)}
      type='button'
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {icon === undefined ? (
        <AIIcon animationActive={decorPaused ? false : undefined} isHovered={isHovered} size={CTA_ICON_SIZE_MAP[size]} sparklesPaint={isSolid ? 'white' : 'brand-gradient'} />
      ) : icon ? (
        <span className='relative z-[1] inline-flex'>{isSolid ? icon : <GradientIcon>{icon}</GradientIcon>}</span>
      ) : null}
      <Typography variant={CTA_LABEL_VARIANT[size]} className={cn('gap-1 break-words font-medium', 'text-foreground', cn(labelStyles(), !icon && 'ml-1.5'))} render={<span />}>
        {label}
      </Typography>
    </button>
  );
});

AIButtonCtaSlot.displayName = 'AIButtonCtaSlot';

// ─── Icon-only (shape === 'rounded' | 'circle') ──────────────────────────────

const aiButtonIconStyles = tv({
  base: 'group relative inline-flex shrink-0 cursor-pointer items-center justify-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-link focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none disabled:ring-0 disabled:saturate-50 disabled:focus-visible:ring-ring',
  variants: {
    variant: {
      solid:
        'bg-[linear-gradient(248deg,#8b5cf6_20%,#0248e9_60%,#001bb8_100%)] text-white shadow-[1px_5px_11px_0px_rgba(139,92,246,0.32)] hover:shadow-[0_0_25px_4px_rgba(139,92,246,0.24)]',
      soft: '',
      ghost: '',
      'ai-outline': 'border border-transparent bg-background text-[#0248e9] shadow-[0_1px_2px_0_rgba(0,27,184,0.05)] hover:shadow-[0_0_25px_4px_rgba(139,92,246,0.24)]',
    },
    size: {
      xs: 'size-6',
      sm: 'size-8',
      md: 'size-10',
      lg: 'size-12',
      xl: 'size-14',
      '2xl': 'size-16',
    },
    shape: {
      rounded: '',
      circle: 'rounded-full',
      cta: '',
    },
  },
  compoundVariants: [
    { size: 'xs', shape: 'rounded', class: 'rounded-md' },
    { size: 'sm', shape: 'rounded', class: 'rounded-lg' },
    { size: 'md', shape: 'rounded', class: 'rounded-[10px]' },
    { size: 'lg', shape: 'rounded', class: 'rounded-xl' },
    { size: 'xl', shape: 'rounded', class: 'rounded-xl' },
    { size: '2xl', shape: 'rounded', class: 'rounded-xl' },
  ],
  defaultVariants: {
    variant: 'solid' as AIButtonVariant,
    size: 'md' as AIButtonSize,
    shape: 'rounded' as AIButtonShape,
  },
});

type AIButtonIconSlotProps = {
  ariaLabel: string;
  className?: string;
  dataTestId?: string;
  decorPaused: boolean;
  icon?: ReactNode;
  isDisabled: boolean;
  isHovered: boolean;
  isSelected: boolean;
  shape: AIButtonShape;
  size: AIButtonSize;
  style?: CSSProperties;
  variant: AIButtonVariant;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

const AIButtonIconSlot = forwardRef<HTMLButtonElement, AIButtonIconSlotProps>(function AIButtonIconSlot(
  { ariaLabel, className, dataTestId, decorPaused, icon, isDisabled, isHovered, isSelected, shape, size, style, variant, onClick, onMouseEnter, onMouseLeave },
  ref
) {
  const useGradientSparkles = variant !== 'solid';
  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      className={cn(aiButtonIconStyles({ variant, size, shape }), className)}
      data-testid={dataTestId ?? 'ai-button'}
      disabled={isDisabled}
      ref={ref}
      style={getVariantStyle(variant, style)}
      type='button'
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {icon === undefined ? (
        <AIIcon
          animationActive={decorPaused ? false : undefined}
          isHovered={isHovered}
          size={ICON_SIZE_MAP[size]}
          sparklesPaint={useGradientSparkles ? 'brand-gradient' : 'white'}
        />
      ) : useGradientSparkles && icon ? (
        <GradientIcon>{icon}</GradientIcon>
      ) : (
        icon
      )}
    </button>
  );
});

AIButtonIconSlot.displayName = 'AIButtonIconSlot';

// ─── AIButton ────────────────────────────────────────────────────────────────

const AIButton = forwardRef<HTMLButtonElement, AIButtonProps>(
  (
    {
      showTooltip = true,
      className,
      dataTestId,
      'data-testid': dataTestIdAttr,
      'aria-label': ariaLabelProp,
      disabled = false,
      size = 'md',
      style,
      variant = 'solid',
      shape = 'rounded',
      icon,
      label,
      onClick = () => {
        void 0;
      },
      isSelected = false,
    },
    ref
  ) => {
    const { t } = useTranslation('ai');
    const [isHovered, setIsHovered] = useState(false);

    const isDisabled = disabled;
    const decorPaused = isDisabled;

    const sharedSlotProps = {
      ref,
      className,
      dataTestId: dataTestIdAttr ?? dataTestId,
      decorPaused,
      icon,
      isDisabled,
      isHovered,
      isSelected,
      style,
      variant,
      onClick,
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    };

    if (shape === 'cta') {
      const ariaLabel = ariaLabelProp ?? (typeof label === 'string' ? label : t('assistant_float.aria_label'));
      return (
        <TooltipOnIntent render={<AIButtonCtaSlot {...sharedSlotProps} ariaLabel={ariaLabel} label={label} size={size} />} disabled={!showTooltip}>
          {t('assistant_float.label')}
        </TooltipOnIntent>
      );
    }

    return (
      <TooltipOnIntent
        render={<AIButtonIconSlot {...sharedSlotProps} ariaLabel={ariaLabelProp ?? t('assistant_float.aria_label')} shape={shape} size={size} />}
        disabled={!showTooltip}
      >
        {t('assistant_float.label')}
      </TooltipOnIntent>
    );
  }
);

AIButton.displayName = 'AIButton';

export default AIButton;
